import { useMemo, useState, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Loader2, Filter } from 'lucide-react'
import PageHeader from '../components/PageHeader'
import AlertSummaryCard from '../components/alerts/AlertSummaryCard'
import AlertDetailPanel from '../components/alerts/AlertDetailPanel'
import { AlertVM, AlertStatus } from '../types/alerts'
import { apiService } from '../services/api'
import { listAlerts as listMockAlerts } from '../services/alertMockService'
import { mapAlertToVM } from '../utils/alertMapper'

export default function Alerts() {
  const navigate = useNavigate()


  // --- Alerts data (from backend) ---
  const [alerts, setAlerts] = useState<AlertVM[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string>('')
  const [confirm, setConfirm] = useState<{ type: 'dismiss' | 'save' | null, alertId?: string, reason?: string }>({ type: null })

  // Fetch alerts from backend
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await apiService.alerts.list({
          sort_by: 'priority',
          limit: 100,
        })
        
        if (response.data.success) {
          const mappedAlerts = (response.data.alerts || []).map(mapAlertToVM)

          if (mappedAlerts.length > 0) {
          setAlerts(mappedAlerts)
          // Auto-select first alert if available and no selection yet
            setSelectedId(prev => prev || mappedAlerts[0].id)
          } else {
            // Successful response but no alerts—fallback to mock data for richer demo experience
            const mockAlerts = await listMockAlerts()
            setAlerts(mockAlerts)
            setSelectedId(prev => prev || (mockAlerts[0]?.id || ''))
          }
        } else {
          setError(response.data.error || 'Failed to load alerts')
          // Use placeholder data for UI development
          const mockAlerts = await listMockAlerts()
          setAlerts(mockAlerts)
          setSelectedId(prev => prev || (mockAlerts[0]?.id || ''))
        }
      } catch (err: any) {
        // Gracefully handle ALL backend errors - use placeholder data for UI development
        // This includes: network errors, CORS errors, 404/500 responses, timeouts, etc.
        
        // Check for various error types (axios errors have different structures)
        const isNetworkError = 
          err.code === 'ECONNREFUSED' || 
          err.code === 'ERR_NETWORK' || 
          err.code === 'ERR_INTERNET_DISCONNECTED' ||
          err.code === 'ECONNABORTED' || // Axios timeout
          err.code === 'ETIMEDOUT' ||
          err.message === 'timeout of 5000ms exceeded' ||
          err.message?.includes('timeout') ||
          err.message?.includes('Network Error') ||
          err.message?.includes('Failed to fetch') ||
          err.message?.includes('CORS') ||
          err.message?.includes('Network request failed') ||
          !err.response || // No response means network/CORS/timeout issue
          (err.request && !err.response) // Axios: request made but no response
        
        // Only log actual errors, not expected connection failures when backend isn't running
        if (!isNetworkError) {
        console.error('Error fetching alerts:', err)
        } else if (import.meta.env.DEV) {
          // In development, use debug level for expected connection errors
          console.debug('[Alerts] Backend not available (using mock data):', err.message || err.code)
        }
        
        // Always use placeholder data on any error for graceful degradation
        const mockAlerts = await listMockAlerts()
        setAlerts(mockAlerts)
        setSelectedId(prev => prev || (mockAlerts[0]?.id || ''))
        setError(null) // Don't show error to user, just use placeholder data
      } finally {
        setLoading(false)
      }
    }

    fetchAlerts()
  }, [])

  // Search, filters, sort
  const [sortBy, setSortBy] = useState<'recent' | 'account_asc' | 'account_desc'>('recent')
  const [statusFilter, setStatusFilter] = useState<'All' | 'New' | 'Opened' | 'Actioned' | 'Dismissed'>('All')
  const [selectedPriorities, setSelectedPriorities] = useState<Array<'Urgent' | 'High' | 'Medium' | 'Low'>>([])
  const [selectedStatuses, setSelectedStatuses] = useState<Array<'New' | 'Opened' | 'Actioned' | 'Dismissed'>>([])
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false)
  const filterDropdownRef = useRef<HTMLDivElement>(null)

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setFilterDropdownOpen(false)
      }
    }
    if (filterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [filterDropdownOpen])

  const filtered = useMemo(() => {
    let list = [...alerts]

    // Apply multi-select status filter (prioritize this over chip filter)
    if (selectedStatuses.length > 0) {
      list = list.filter(a => {
        if (selectedStatuses.includes('Actioned')) {
          return selectedStatuses.includes(a.status as any) || a.status === 'Saved'
        }
        return selectedStatuses.includes(a.status as any)
      })
    } else if (statusFilter !== 'All') {
      // Fall back to chip filter if no multi-select is active
      if (statusFilter === 'Actioned') {
        list = list.filter(a => a.status === 'Actioned' || a.status === 'Saved')
      } else {
        list = list.filter(a => a.status === statusFilter)
      }
    }

    // Apply multi-select priority filter
    if (selectedPriorities.length > 0) {
      list = list.filter(a => {
        return a.priority && selectedPriorities.includes(a.priority)
      })
    }

    list = [...list]
    if (sortBy === 'recent') {
      // Sort by: status = "New" first, then by recency (latest first)
      list.sort((a, b) => {
        // Prioritize "New" status
        if (a.status === 'New' && b.status !== 'New') return -1
        if (a.status !== 'New' && b.status === 'New') return 1
        // Then sort by recency (newest first)
        return +new Date(b.createdAt) - +new Date(a.createdAt)
      })
    } else if (sortBy === 'account_asc') {
      list.sort((a, b) => a.accountName.localeCompare(b.accountName))
    } else if (sortBy === 'account_desc') {
      list.sort((a, b) => b.accountName.localeCompare(a.accountName))
    }

    return list
  }, [alerts, statusFilter, sortBy, selectedPriorities, selectedStatuses])

  const selected = useMemo(() => filtered.find(a => a.id === selectedId) || filtered[0] || null, [filtered, selectedId])

  const counts = useMemo(() => {
    const newCount = alerts.filter(a => a.status === 'New').length
    const openedCount = alerts.filter(a => a.status === 'Opened').length
    const actionedCount = alerts.filter(a => a.status === 'Actioned' || a.status === 'Saved').length
    const dismissedCount = alerts.filter(a => a.status === 'Dismissed').length

    return {
      newCount,
      openedCount,
      actionedCount,
      dismissedCount,
    }
  }, [alerts])

  const statusCounts: Record<'New' | 'Opened' | 'Actioned' | 'Dismissed', number> = {
    New: counts.newCount,
    Opened: counts.openedCount,
    Actioned: counts.actionedCount,
    Dismissed: counts.dismissedCount,
  }

  const summaryChipStyles: Record<'New' | 'Opened' | 'Actioned' | 'Dismissed', { base: string; active: string }> = {
    New: {
      base: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
      active: 'bg-blue-100 text-blue-800 border-blue-300 ring-2 ring-offset-1 ring-blue-300',
    },
    Opened: {
      base: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100',
      active: 'bg-indigo-100 text-indigo-800 border-indigo-300 ring-2 ring-offset-1 ring-indigo-300',
    },
    Actioned: {
      base: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
      active: 'bg-green-100 text-green-800 border-green-300 ring-2 ring-offset-1 ring-green-300',
    },
    Dismissed: {
      base: 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200',
      active: 'bg-gray-200 text-gray-800 border-gray-300 ring-2 ring-offset-1 ring-gray-300',
    },
  }

  const handleToggleSave = async (id: string, nextSaved: boolean) => {
    // Optimistic UI update
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, status: nextSaved ? 'Actioned' : 'New' } : a))
    
    // Try to call API, but don't fail if backend is not connected
    try {
      if (nextSaved) {
        await apiService.alerts.action(id, { action: 'other', notes: 'Saved for later' })
      } else {
        await apiService.alerts.updateStatus(id, 'New')
      }
    } catch (err: any) {
      // Silently handle errors - UI already updated optimistically
      if (err.code !== 'ECONNREFUSED' && err.code !== 'ERR_NETWORK' && !err.message?.includes('Network Error')) {
        console.error('Error saving alert:', err)
      }
    }
  }

  const handleSave = (id: string) => setConfirm({ type: 'save', alertId: id })
  
  const handleDismiss = (id: string, reason?: string) => setConfirm({ type: 'dismiss', alertId: id, reason })
  
  const handleOpen = async (id: string) => {
    setSelectedId(id)
    // Auto-update status to "Opened" if currently "New" (optimistic update)
    const alert = alerts.find(a => a.id === id)
    if (alert && alert.status === 'New') {
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, status: 'Opened' } : a))
      // Try to call API, but don't fail if backend is not connected
      try {
        await apiService.alerts.updateStatus(id, 'Opened')
      } catch (err: any) {
        // Silently handle errors - UI already updated optimistically
        if (err.code !== 'ECONNREFUSED' && err.code !== 'ERR_NETWORK' && !err.message?.includes('Network Error')) {
          console.error('Error updating alert status:', err)
        }
      }
    }
  }

  const applyConfirm = async () => {
    if (!confirm.type || !confirm.alertId) return
    
    // Optimistic UI update
    if (confirm.type === 'save') {
      setAlerts(prev => prev.map(a => a.id === confirm.alertId ? { ...a, status: 'Actioned' } : a))
    } else {
      setAlerts(prev => prev.map(a => a.id === confirm.alertId ? { ...a, status: 'Dismissed' } : a))
      if (selectedId === confirm.alertId) {
        const remaining = filtered.filter(a => a.id !== confirm.alertId)
        setSelectedId(remaining[0]?.id || '')
      }
    }
    
    // Try to call API, but don't fail if backend is not connected
    try {
      if (confirm.type === 'save') {
        await apiService.alerts.action(confirm.alertId, { action: 'other', notes: 'Saved for later' })
      } else {
        // Map frontend dismiss reasons to backend format
        const reasonMap: Record<string, string> = {
          'Not relevant': 'not_relevant',
          'Duplicate': 'other',
          'Already addressed': 'already_have',
          'Wrong account': 'other',
          'Other': 'other',
        }
        const backendReason = reasonMap[confirm.reason || 'Other'] || 'other'
        await apiService.alerts.dismiss(confirm.alertId, { reason: backendReason, notes: confirm.reason })
      }
    } catch (err: any) {
      // Silently handle errors - UI already updated optimistically
      if (err.code !== 'ECONNREFUSED' && err.code !== 'ERR_NETWORK' && !err.message?.includes('Network Error')) {
        console.error('Error applying action:', err)
      }
    } finally {
      setConfirm({ type: null })
    }
  }

  const cancelConfirm = () => setConfirm({ type: null })

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="shrink-0">
      <PageHeader title="Alerts">
        <button
          onClick={() => navigate('/alerts')}
          className="hidden"
        >
          Alerts
        </button>
      </PageHeader>
          </div>

      <div className="flex-1 flex flex-col overflow-hidden min-h-0 px-5 py-4">
        {/* Alerts layout */}
        <div className="flex-1 grid grid-cols-12 gap-5 min-h-0">
          <aside className="col-span-12 md:col-span-4 xl:col-span-3 flex flex-col min-h-0">
            <div className="flex-1 bg-white border border-gray-200 rounded-xl flex flex-col min-h-0 shadow-sm">
              <div className="px-3 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3 shrink-0">
                <div className="flex flex-wrap items-center gap-1.5 text-[13px]">
                <button
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-medium ${selectedStatuses.length === 0 && statusFilter === 'All' ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'}`}
                    onClick={() => {
                      setStatusFilter('All')
                      setSelectedStatuses([])
                    }}
                >
                    All
                    <span className="text-xs font-semibold">{alerts.length}</span>
                </button>
                  {(['New', 'Opened', 'Actioned', 'Dismissed'] as const).map(label => {
                    const isActive = selectedStatuses.length === 0 && statusFilter === label
                    return (
                      <button
                        key={label}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-medium ${isActive ? summaryChipStyles[label].active : summaryChipStyles[label].base}`}
                        onClick={() => {
                          setStatusFilter(label)
                          if (label === 'Actioned') {
                            setSelectedStatuses(['Actioned'])
                          } else {
                            setSelectedStatuses([label as 'New' | 'Opened' | 'Dismissed'])
                          }
                        }}
                      >
                        {label}
                        <span className="text-xs font-semibold">{statusCounts[label]}</span>
                      </button>
                    )
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <select className="h-9 text-sm border border-gray-200 rounded-lg px-3 text-gray-700" value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
                  <option value="recent">Most recent</option>
                  <option value="account_asc">Account A → Z</option>
                  <option value="account_desc">Account Z → A</option>
                </select>
                <div className="relative" ref={filterDropdownRef}>
                  <button
                    onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                      className="flex items-center gap-1.5 h-9 text-sm border border-gray-200 rounded-lg px-3 text-gray-700 hover:bg-gray-50"
                  >
                    <Filter className="w-4 h-4" />
                    Filter
                    {(selectedPriorities.length > 0 || selectedStatuses.length > 0) && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-tandem-blue text-white rounded-full">
                        {selectedPriorities.length + selectedStatuses.length}
                      </span>
                    )}
                  </button>
                  {filterDropdownOpen && (
                      <div className="absolute top-full right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                      <div className="p-2">
                        {/* Priority Filter */}
                        <div className="mb-3">
                          <div className="px-2 py-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                            Priority
                          </div>
                          <div className="space-y-1">
                            {(['Urgent', 'High', 'Medium', 'Low'] as const).map(priority => (
                              <label key={priority} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedPriorities.includes(priority)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedPriorities([...selectedPriorities, priority])
                                    } else {
                                      setSelectedPriorities(selectedPriorities.filter(p => p !== priority))
                                    }
                                  }}
                                  className="w-4 h-4 text-tandem-blue border-gray-300 rounded focus:ring-tandem-blue"
                                />
                                <span className="text-sm text-gray-700">{priority}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        {/* Status Filter */}
                        <div>
                          <div className="px-2 py-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                            Status
                          </div>
                          <div className="space-y-1">
                            {(['New', 'Opened', 'Actioned', 'Dismissed'] as const).map(status => (
                              <label key={status} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedStatuses.includes(status)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedStatuses([...selectedStatuses, status])
                                    } else {
                                      setSelectedStatuses(selectedStatuses.filter(s => s !== status))
                                    }
                                  }}
                                  className="w-4 h-4 text-tandem-blue border-gray-300 rounded focus:ring-tandem-blue"
                                />
                                <span className="text-sm text-gray-700">{status}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        {/* Clear Filters */}
                        {(selectedPriorities.length > 0 || selectedStatuses.length > 0) && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <button
                              onClick={() => {
                                setSelectedPriorities([])
                                setSelectedStatuses([])
                                setStatusFilter('All')
                              }}
                              className="w-full text-sm text-gray-600 hover:text-gray-900 px-2 py-1.5 hover:bg-gray-50 rounded"
                            >
                              Clear all filters
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              </div>
              <ul className="flex-1 overflow-y-auto divide-y divide-gray-100 min-h-0">
                {loading ? (
                  <li className="p-6 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    <span className="ml-2 text-sm text-gray-500">Loading alerts...</span>
                  </li>
                ) : error ? (
                  <li className="p-6 text-sm text-red-600">
                    <p className="font-medium">Error loading alerts</p>
                    <p className="mt-1 text-xs text-gray-600">{error}</p>
                  </li>
                ) : filtered.length > 0 ? (
                  // Show filtered alerts if any exist
                  filtered.map(a => (
                    <AlertSummaryCard key={a.id} alert={a} selected={selected?.id === a.id} onOpen={handleOpen} onToggleSave={handleToggleSave} onDismiss={handleDismiss} />
                  ))
                ) : alerts.filter(a => a.status === 'New').length === 0 ? (
                  // No "New" alerts - show empty state
                  <li className="p-6 text-center">
                    <p className="text-sm text-gray-500 mb-2">No new alerts.</p>
                    <p className="text-xs text-gray-400">
                      {alerts.length === 0 
                        ? 'Go to Setup to connect your data sources and generate alerts.'
                        : 'All alerts have been reviewed. Check other status filters to see more.'}
                    </p>
                  </li>
                ) : (
                  // Filtered list is empty but alerts exist (filter mismatch)
                  <li className="p-6 text-sm text-gray-500">No alerts match the current filters.</li>
                )}
              </ul>
            </div>
          </aside>

          <section className="col-span-12 md:col-span-8 xl:col-span-9 flex flex-col min-h-0">
            <div className="flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col min-h-0">
              {!selected ? (
                <div className="m-auto text-gray-400 text-sm">Select an alert to see details</div>
              ) : (
                <AlertDetailPanel alert={selected} onSave={handleSave} onClose={() => setSelectedId('')} />
              )}
            </div>
          </section>
        </div>
      </div>

      {confirm.type && confirm.alertId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={cancelConfirm}></div>
          <div className="relative bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-md p-6">
            <div className="flex items-start justify-between">
              <h4 className="text-lg font-semibold text-gray-900">
                {confirm.type === 'save' ? 'Save this alert?' : 'Dismiss this alert?'}
              </h4>
              <button className="p-2 text-gray-500 hover:text-gray-700" onClick={cancelConfirm}><X className="w-4 h-4" /></button>
            </div>
            {confirm.type === 'dismiss' && confirm.reason && (
              <p className="mt-1 text-xs text-gray-500">Reason: {confirm.reason}</p>
            )}
            <p className="mt-2 text-sm text-gray-600">
              {confirm.type === 'save'
                ? 'Saved alerts will appear in your list for follow-up later.'
                : 'You can find dismissed alerts in the tracker later if needed.'}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50" onClick={cancelConfirm}>Cancel</button>
              <button
                className={`${confirm.type === 'save' ? 'bg-tandem-blue hover:bg-blue-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'} px-3 py-2 text-sm rounded-lg`}
                onClick={applyConfirm}
              >
                {confirm.type === 'save' ? 'Save' : 'Dismiss'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

